import { useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { XStack, YStack } from 'tamagui';

import { SolidSurface } from '@/components/glass-surface';
import { Text } from '@/components/text';
import { TextLink } from '@/components/text-link';
import { nextProfileStep, nudgeCopy, shouldShowNudge, type ProfileStep } from '@/lib/profile-nudge';
import { dismissStep, getDismissedStep } from '@/lib/profile-nudge-storage';
import { space } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// One quiet suggestion at the top of the profile (issue #245): the single
// best missing thing, one action, and "Not now". No bar, ring or percentage
// -- the "Profile content" rows below already show the counts. Renders
// nothing while the dismissed state loads (so it never pops in), when the
// profile is complete, or after a dismiss until the next step comes up.
export function ProfileNudgeCard({ photoCount, promptCount }: { photoCount: number; promptCount: number }) {
  const router = useRouter();
  // null = still reading storage.
  const [dismissed, setDismissed] = useState<string | undefined | null>(null);

  useEffect(() => {
    getDismissedStep()
      .then(setDismissed)
      .catch(() => setDismissed(undefined));
  }, []);

  const step = nextProfileStep({ photoCount, promptCount });

  if (dismissed === null || !shouldShowNudge(step, dismissed)) return null;

  const current = step as ProfileStep;
  const copy = nudgeCopy(current);

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$2" accessibilityRole="summary" accessibilityLabel="Suggested next step">
        <Text
          color="$colorTertiary"
          fontSize="$meta"
          lineHeight="$meta"
          fontWeight="$semibold"
          textTransform="uppercase"
          letterSpacing={0.4}
        >
          Next step
        </Text>
        <Text color="$color">{copy.text}</Text>
        <XStack alignItems="center" justifyContent="space-between" pt="$1">
          <TextLink
            {...hit(12)}
            onPress={() => router.push(current === 'photo' ? '/profile-photos' : '/profile-prompts')}
          >
            {copy.action}
          </TextLink>
          <TextLink
            {...hit(12)}
            color="$colorTertiary"
            onPress={() => {
              setDismissed(current);
              void dismissStep(current);
            }}
          >
            Not now
          </TextLink>
        </XStack>
      </YStack>
    </SolidSurface>
  );
}
